import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useAlert } from '../context/AlertContext';

const StatutoryReportForm = ({ hazard, reportType, onClose }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [formData, setFormData] = useState({
    // Common fields
    injured_person_name: '',
    injured_person_age: '',
    injured_person_gender: '',
    injured_person_department: '',

    // India specific
    factory_name: '',
    factory_address: '',
    factory_license_no: '',
    manager_name: '',

    // Singapore specific
    company_uen: '',
    injured_person_nric: '',
    injured_person_dob: '',
    injured_person_designation: '',
    employment_type: 'Permanent'
  });

  const [generating, setGenerating] = useState(false);
  const [loadingCompanyData, setLoadingCompanyData] = useState(true);

  // Fetch company settings to pre-populate form
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/company/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data) {
          const company = response.data;
          setFormData(prev => ({
            ...prev,
            // Pre-populate India fields from company settings
            factory_name: company.company_name || '',
            factory_address: company.address || '',
            factory_license_no: company.factory_license_no || '',
            manager_name: company.manager_name || ''
          }));
        }
      } catch (err) {
        console.log('Could not fetch company settings for pre-population');
      } finally {
        setLoadingCompanyData(false);
      }
    };

    fetchCompanySettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerate = async () => {
    // Validation
    if (reportType === 'india') {
      if (!formData.injured_person_name || !formData.factory_name || !formData.manager_name) {
        showError('Please fill in all required fields');
        return;
      }
    } else {
      if (!formData.injured_person_name || !formData.injured_person_nric || !formData.company_uen) {
        showError('Please fill in all required fields');
        return;
      }
    }

    setGenerating(true);

    try {
      const token = localStorage.getItem('token');
      const endpoint = reportType === 'india' 
        ? `/hazards/${hazard.id}/statutory-reports/india-form18`
        : `/hazards/${hazard.id}/statutory-reports/singapore-ireport`;
      
      // Send additional data as query params or headers
      const response = await axios.post(
        `${API}${endpoint}`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = reportType === 'india' 
        ? `Form18_${hazard.id.substring(0, 8)}.pdf`
        : `Singapore_iReport_${hazard.id.substring(0, 8)}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess(`${reportType === 'india' ? 'India Form 18' : 'Singapore MOM iReport'} generated successfully!`);
      onClose();
    } catch (err) {
      showError('Failed to generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full my-8 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-[#1B5C94]">
              {reportType === 'india' ? '🇮🇳 India Form 18' : '🇸🇬 Singapore MOM iReport'} - Data Entry
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
            {loadingCompanyData && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                Loading company details...
              </div>
            )}

            {/* Incident Info (Read-only) */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-bold text-gray-800 mb-2">Incident Information</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="font-semibold">Title:</span> {hazard.title}</div>
                <div><span className="font-semibold">Date:</span> {hazard.incident_date}</div>
                <div><span className="font-semibold">Type:</span> {hazard.hazard_type}</div>
                <div><span className="font-semibold">Severity:</span> {hazard.severity}</div>
              </div>
            </div>

            {/* Injured Person Details */}
            <div>
              <h3 className="font-bold text-gray-800 mb-3">Injured Person Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="injured_person_name"
                    value={formData.injured_person_name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                    placeholder="Enter full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Age <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="injured_person_age"
                    value={formData.injured_person_age}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                    placeholder="Age"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="injured_person_gender"
                    value={formData.injured_person_gender}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Department
                  </label>
                  <input
                    type="text"
                    name="injured_person_department"
                    value={formData.injured_person_department}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                    placeholder="Department"
                  />
                </div>
              </div>
            </div>

            {/* India-specific fields */}
            {reportType === 'india' && (
              <div>
                <h3 className="font-bold text-gray-800 mb-3">Factory Details (India)</h3>
                <p className="text-xs text-gray-500 mb-3">
                  These fields are pre-populated from Admin Settings. You can modify them if needed.
                </p>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Factory Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="factory_name"
                      value={formData.factory_name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      placeholder="Registered factory name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Factory Address
                    </label>
                    <input
                      type="text"
                      name="factory_address"
                      value={formData.factory_address}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      placeholder="Complete address"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        License Number
                      </label>
                      <input
                        type="text"
                        name="factory_license_no"
                        value={formData.factory_license_no}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                        placeholder="Factory license no."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Manager Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="manager_name"
                        value={formData.manager_name}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                        placeholder="Factory manager"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Singapore-specific fields */}
            {reportType === 'singapore' && (
              <div>
                <h3 className="font-bold text-gray-800 mb-3">Singapore-Specific Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Company UEN <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="company_uen"
                      value={formData.company_uen}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      placeholder="Unique Entity Number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      NRIC/FIN <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="injured_person_nric"
                      value={formData.injured_person_nric}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      placeholder="Worker NRIC/FIN"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      name="injured_person_dob"
                      value={formData.injured_person_dob}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Designation
                    </label>
                    <input
                      type="text"
                      name="injured_person_designation"
                      value={formData.injured_person_designation}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      placeholder="Job title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Employment Type
                    </label>
                    <select
                      name="employment_type"
                      value={formData.employment_type}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                    >
                      <option value="Permanent">Permanent</option>
                      <option value="Contract">Contract</option>
                      <option value="Temporary">Temporary</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6 pt-4 border-t">
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="flex-1 bg-[#1B5C94] text-white font-bold py-3 px-6 rounded-lg hover:bg-[#164A7B] transition-colors disabled:opacity-50"
            >
              {generating ? 'Generating PDF...' : 'Generate Report'}
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-200 text-gray-700 font-bold rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatutoryReportForm;
